import { Checkbox, type CheckboxProps } from '@affine/component';
import {
  streamLainAnimationController,
  type StreamLainAnimationHandle,
} from '@blocksuite/affine-shared/utils';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import {
  type CSSProperties,
  type HTMLAttributes,
  useLayoutEffect,
  useRef,
} from 'react';

import * as styles from './selection-checkbox.css';

export type { RangeSelectionInput, SelectionChange } from './range-selection';
export {
  applyRangeSelection,
  replaceSelectionKey,
  toggleSelectionKey,
} from './range-selection';

export type SelectionCheckboxProps = CheckboxProps;

export function SelectionCheckbox({
  checked,
  className,
  indeterminate,
  ...props
}: SelectionCheckboxProps) {
  return (
    <Checkbox
      {...props}
      checked={checked}
      className={clsx(styles.selectionCheckbox, className)}
      data-checked={checked ? 'true' : undefined}
      data-indeterminate={indeterminate ? 'true' : undefined}
      indeterminate={indeterminate}
    />
  );
}

function toCssLength(value: number | string) {
  return typeof value === 'number' ? `${value}px` : value;
}

// a type alias, so it passes as a Web Animations Keyframe
type SlotFrame = {
  width: string;
  marginRight: string;
  opacity: string;
};

function currentFrame(element: HTMLElement): SlotFrame {
  const current = getComputedStyle(element);
  return {
    width: current.width,
    marginRight: current.marginRight,
    opacity: current.opacity,
  };
}

export type AnimatedSelectionSlotProps = HTMLAttributes<HTMLDivElement> & {
  checked: boolean;
  visible: boolean;
  checkboxProps?: Omit<SelectionCheckboxProps, 'checked'>;
  width?: number | string;
  gap?: number | string;
  hiddenMarginRight?: number | string;
  slotHeight?: number | string;
};

export function AnimatedSelectionSlot({
  checked,
  visible,
  checkboxProps,
  width = 20,
  gap = 12,
  hiddenMarginRight = 0,
  slotHeight = 20,
  className,
  style,
  ...props
}: AnimatedSelectionSlotProps) {
  const elementRef = useRef<HTMLDivElement>(null);
  const motion = useRef<StreamLainAnimationHandle | null>(null);
  // The values written last. A list mounts and toggles hundreds of slots in
  // one commit; reading computed style in each slot's layout effect after the
  // previous slot wrote its style forced one layout per row. The slot reads
  // layout only while its own animation runs, to start from the visible state.
  const applied = useRef<SlotFrame | null>(null);
  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    const to: SlotFrame = {
      width: visible ? toCssLength(width) : '0px',
      marginRight: toCssLength(visible ? gap : hiddenMarginRight),
      opacity: visible ? '1' : '0',
    };
    const running = motion.current;
    const from = running ? currentFrame(element) : applied.current;
    running?.cancel();
    motion.current = null;
    Object.assign(element.style, to);
    applied.current = to;
    if (!from) return;
    const handle = streamLainAnimationController.play(
      element,
      [from, to],
      'resize'
    );
    motion.current = handle;
    void handle.finished.then(() => {
      if (motion.current === handle) motion.current = null;
    });
  }, [gap, hiddenMarginRight, visible, width]);
  useLayoutEffect(() => () => motion.current?.cancel(), []);

  const slotStyle: CSSProperties = {
    ...assignInlineVars({
      [styles.animatedSelectionSlotWidth]: toCssLength(width),
      [styles.animatedSelectionSlotGap]: toCssLength(gap),
      [styles.animatedSelectionSlotHiddenMarginRight]:
        toCssLength(hiddenMarginRight),
      [styles.animatedSelectionSlotHeight]: toCssLength(slotHeight),
    }),
    ...style,
  };

  return (
    <div
      ref={elementRef}
      className={clsx(styles.animatedSelectionSlot, className)}
      data-visible={visible ? 'true' : undefined}
      style={slotStyle}
      {...props}
    >
      <SelectionCheckbox checked={checked} {...checkboxProps} />
    </div>
  );
}
